import { useMemo, useState } from 'react'
import { PricingCard } from './pricing-card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { BillingCycle, PlanId } from '@db8/shared'

const plans: any = [
  {
    id: 'PRO',
    title: 'Pro Tier',
    subtitle: 'Perfect for classrooms and formal debates',
    monthlyPrice: 20,
    yearlyPrice: 180,
    perdebatePrice: 7,
    theme: 'affirmative',
    features: [
      'Everything in Free Tier',
      'Additional Formats: LD, Public Forum (Coming Soon), Policy (Coming Soon)',
      'One debate at a time',
      'All early adopters are a part of the Founders Circle with access to technical support and a direct channel to the engineering team to help us build out the platform!',
    ],
    showPreviewBadge: true,
  },
]

export const PricingSection = ({
  selectedPlan,
  setSelectedPlan,
  billingCycle = 'monthly',
  setBillingCycle,
  buttonMode = 'buy',
  compact = false,
}: {
  selectedPlan?: PlanId
  setSelectedPlan?: (planId: PlanId) => void
  billingCycle?: BillingCycle
  setBillingCycle?: (cycle: BillingCycle) => void
  buttonMode?: 'buy' | 'select'
  compact?: boolean
}) => {
  const isControlled = selectedPlan && setSelectedPlan && billingCycle && setBillingCycle

  const [localPlan, setLocalPlan] = useState<PlanId>('PRO')
  const [localCycle, setLocalCycle] = useState<BillingCycle>('monthly')

  const selected = isControlled ? selectedPlan : localPlan
  const cycle = isControlled ? billingCycle : localCycle

  const handlePlanSelect = (planId: PlanId) => {
    isControlled ? setSelectedPlan!(planId) : setLocalPlan(planId)
  }

  const handleCycleChange = (value: BillingCycle) => {
    isControlled ? setBillingCycle!(value) : setLocalCycle(value)
  }

  const getVariant = useMemo(() => {
    switch (selected) {
      case 'PRO':
        return 'green'
      case 'PREMIUM':
        return 'purple'
      default:
        return 'green'
    }
  }, [selected])

  return (
    <section>
      <div className={`flex justify-center items-center gap-3 ${compact ? '' : 'mt-6 mb-4'}`}>
        <ToggleGroup
          type="single"
          value={cycle}
          onValueChange={(val: BillingCycle) => val && handleCycleChange(val)}
        >
          <ToggleGroupItem value="monthly" variant={getVariant}>
            Monthly
          </ToggleGroupItem>
          <ToggleGroupItem value="yearly" variant={getVariant}>
            Yearly
          </ToggleGroupItem>
          <ToggleGroupItem value="perdebate" variant={getVariant} disabled>
            Per Debate
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className={`w-full grid gap-6 grid-cols-1 place-items-center pt-6`}>
        {plans.map((plan) => (
          <PricingCard
            key={plan.id}
            title={plan.title}
            subtitle={plan.subtitle}
            price={plan[`${cycle}Price`]}
            frequency={cycle === 'monthly' ? '/mo' : cycle === 'yearly' ? '/yr' : '/debate'}
            buttonLabel={
              buttonMode === 'select' ? (selected === plan.id ? 'Selected' : 'Choose') : 'Buy'
            }
            buttonLink={
              buttonMode === 'buy'
                ? `/paddle-checkout?plan=${plan.id.toLowerCase()}&cycle=${cycle.toLowerCase()}`
                : ''
            }
            features={
              cycle === 'perdebate'
                ? plan.features.filter((f) => !f.toLowerCase().includes('pack debates per month'))
                : plan.features
            }
            theme={plan.theme}
            showPreviewBadge={plan.showPreviewBadge}
            className={`transition-all bg-white antialiased ${
              compact
                ? `${
                    plan.id === 'PRO'
                      ? 'scale-y-[0.80] scale-x-[0.90]'
                      : 'scale-y-[0.75] scale-x-[0.85] '
                  } transform -mt-16 -mb-12 `
                : ''
            } ${
              selected !== plan.id && buttonMode === 'select' ? 'opacity-50' : ''
            } ${plan.id === 'PRO' ? 'ring-2 ring-primary scale-105' : ''}`}
            onClick={() => handlePlanSelect(plan.id)}
          />
        ))}
      </div>
    </section>
  )
}
